import React from 'react';
import {
  AIContent,
  isApiResult,
  isSuggestedQuestionsResult,
  SearchResultCheckType,
} from '../../../../libs/models/conversations/ChatMessage';
import { FunctionCallContent, isFunctionCallContent } from '../../../../libs/models/agui/FunctionCallContent';
import { IMCPResponse, IMCPContext } from '../../../../libs/models/app/mcp/Tool';
import { MemberCards } from '../hrmaster/MemberCard';
import { ChatPluginStarter } from '../../../../pages/chat/unit/ChatPluginStarter';
import MultiSearch from '../web-search/MultiSearch';
import { ChatHistoryToolResult } from '../../../../pages/chat/chat-history/ChatHistoryToolResult';
import { isFrontManagedTool } from '../../../../redux/features/message-relay/agui/agUiHelpers';
import { useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';

interface Props {
  result: unknown;
  callId?: string;
  contents: AIContent[] | undefined;
  authorUserId?: string;
  onSuggestedQuestionClick?: (text: string) => void;
}

function findFunctionCallByCallId(
  contents: AIContent[] | undefined,
  callId: string | undefined,
): FunctionCallContent | undefined {
  if (!contents || !callId) return undefined;
  const call = contents.find(
    (c): c is FunctionCallContent =>
      isFunctionCallContent(c) && c.callId === callId && c.name !== 'request_approval',
  );
  if (call) return call;
  const approval = contents.find((c) => {
    const t = (c as { $type?: string }).$type;
    return (
      (t === 'toolApprovalRequest' || t === 'toolApprovalResponse') &&
      (c as { toolCall?: { callId?: string } }).toolCall?.callId === callId
    );
  }) as { toolCall?: FunctionCallContent } | undefined;
  return approval?.toolCall;
}

/** 도구 결과를 카드용으로 변환. */
function toContent(result: unknown): IMCPContext[] | undefined {
  const r = result as {
    content?: IMCPContext[] | string;
    stdout?: string;
    stderr?: string;
    error?: string;
  };
  if (Array.isArray(r?.content)) return r.content; // MCP
  if (typeof r?.content === 'string') return [{ type: 'text', text: r.content }]; // 리소스 읽기
  if (r?.stdout != null || r?.stderr != null) {
    const text = [r.stdout, r.stderr].filter(Boolean).join('\n'); // 스크립트 실행
    return [{ type: 'text', text }];
  }
  if (r?.error) return [{ type: 'text', text: r.error }];
  return undefined;
}

/** functionResult  */
function toMcpResponse(contents: AIContent[] | undefined, result: unknown, callId: string | undefined): IMCPResponse {
  const matchedCall = findFunctionCallByCallId(contents, callId);
  return {
    toolName: matchedCall?.name ?? '',
    arguments: (matchedCall?.arguments ?? {}) as Record<string, object>,
    content: toContent(result),
  };
}
/** functionResult 의 result 타입별 렌더를 한곳에서 분기 */
export const FunctionResultRenderer: React.FC<Props> = ({
  result,
  callId,
  contents,
  authorUserId,
  onSuggestedQuestionClick,
}) => {
  const tools = useAppSelector((s: RootState) => s.tools);

  // Hrmaster 멤버 카드
  if (authorUserId === 'Agent:Hrmaster' && isApiResult(result)) {
    return <MemberCards functionResults={result} />;
  }

  // 추천 질문
  if (isSuggestedQuestionsResult(result)) {
    return (
      <ChatPluginStarter
        starters={result.data}
        isDisabled={false}
        useInfo={false}
        onClick={(text: string) => onSuggestedQuestionClick?.(text)}
      />
    );
  }

  // 웹 검색 결과
  if (authorUserId !== 'Agent:Hrmaster' && SearchResultCheckType(result)) {
    return (
      <>
        {result.results.map((r, i) => (
          <React.Fragment key={i}>
            {r.hasError || (r.sourceType && <MultiSearch results={r} isAccordionOpen={false} />)}
          </React.Fragment>
        ))}
      </>
    );
  }

  // 일반 도구 결과 (MCP / 스킬 리소스·스크립트) — 프론트가 관리하는 도구만 표시
  const mcpResponse = toMcpResponse(contents, result, callId);
  if (!mcpResponse.toolName && !mcpResponse.content) return null;
  // 메일 발송은 functionCall 쪽 MailApprovalCard 로 표시하므로 결과 카드는 숨김 (중복 방지)
  if (mcpResponse.toolName === 'OutlookMailConnector_SendEmail') return null;
  if (!isFrontManagedTool(mcpResponse.toolName, tools)) return null;
  return <ChatHistoryToolResult toolName={mcpResponse.toolName} result={mcpResponse} />;
};
